import { useAuthStore } from './authStore';

import type { AuthUser, LoginData } from './authStore';

/**
 * AppId del tenant de plataforma (pymestock-default). Espeja
 * TenantConstants.DefaultAppId del backend.
 */
export const PLATFORM_APP_ID = '00000000-0000-0000-0000-000000000010';

/** El backend acepta el rol con y sin capitalizar segun el origen del token. */
export function isAdminRole(role: string | undefined): boolean {
  return role?.toLowerCase() === 'admin';
}

/** Fine-grained selector: current authenticated user or null */
export const useAuthUser = (): AuthUser | null => useAuthStore((s) => s.user);

/**
 * Admin del tenant propio: puede gestionar SUS usuarios, no la plataforma.
 */
export const useIsTenantAdmin = (): boolean =>
  useAuthStore((s) => isAdminRole(s.user?.role));

/**
 * Admin de plataforma (PymeCode): unico que puede administrar tenants/aplicaciones.
 *
 * Espeja [PlatformAdminOnly] del backend, que exige DOS condiciones: app_id igual
 * al tenant de plataforma Y rol Admin. Mirar solo el rol deja que el Admin de
 * cualquier tenant vea la administracion de aplicaciones.
 */
export const useIsPlatformAdmin = (): boolean =>
  useAuthStore((s) => isAdminRole(s.user?.role) && s.user?.appId === PLATFORM_APP_ID);

/** Fine-grained selector: whether the user is authenticated */
export const useIsAuthenticated = (): boolean => useAuthStore((s) => s.isAuthenticated);

/** Fine-grained selector: current access token */
export const useAccessToken = (): string | null => useAuthStore((s) => s.accessToken);

/** Fine-grained selector: login action */
export const useLogin = (): ((data: LoginData) => void) => useAuthStore((s) => s.login);

/** Fine-grained selector: logout action */
export const useLogout = (): (() => void) => useAuthStore((s) => s.logout);

/**
 * Convenience hook that exposes the most commonly needed auth values.
 * Use fine-grained selectors above when you only need one value.
 */
export function useAuth() {
  const user = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const accessToken = useAccessToken();
  const login = useLogin();
  const logout = useLogout();

  return { user, isAuthenticated, accessToken, login, logout };
}
